import { type BaseRendererComponentProps, BaseModel, BaseModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import React from 'react';
import { IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import {
    AI_VIEWER_TOOLBAR_ID,
    closeLimit,
    isAiPhotoImproveAttachesEnabled,
    isAiPhotoImproveEnabled,
    isAiPhotoImprovePublicEnabled,
    PROMO_VIEWER_POPUP_ID,
    showInterval,
    showLimit,
    storageKeyForPopup,
} from 'reactApp/appHelpers/featuresHelpers/features/aiPhotoImprove';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { store as reduxStore } from 'reactApp/store';
import { sendXray } from 'reactApp/utils/ga';

class PopupModel extends BaseModel<BaseModelProps> {
    getParent(): HTMLElement | null {
        return document.documentElement;
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/ReactViewer/AiImprove/AiImproveViewerPromoPopupRenderer'));

const SuspendedComponent = (props: BaseRendererComponentProps) => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent {...props} />
        </React.Suspense>
    );
};

export const aiImproveViewerPromoPopupCreator: ModelCreator = ({ models, render, stores }) =>
    new PopupModel(
        PROMO_VIEWER_POPUP_ID,
        models.base,
        new BaseModelProps(PROMO_VIEWER_POPUP_ID, {
            store: new LocalStorageStore(storageKeyForPopup, stores.localStorage),
            renderer: new ReactRenderer(PROMO_VIEWER_POPUP_ID, SuspendedComponent, render.react),
            closable: true,
            showLimit,
            closeLimit,
            showInterval,
            templateData: {},
            ignoreTestOnRedraw: true,
            markAsCompleted: true,
            test: (resolve, reject) => {
                if (IS_PHONE_BROWSER) {
                    reject();
                    return;
                }

                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);
                const isViewerActive = ViewerSelectors.isViewerActive(state);

                if (!isAiPhotoImproveEnabled || !isViewerActive || !document.getElementById(AI_VIEWER_TOOLBAR_ID)) {
                    reject();
                    return;
                }

                if (storage === EStorageType.public && !isAiPhotoImprovePublicEnabled) {
                    reject();
                    return;
                }

                if (storage === EStorageType.viewerAttaches && !isAiPhotoImproveAttachesEnabled) {
                    reject();
                    return;
                }

                if (
                    ![
                        EStorageType.home,
                        EStorageType.favorites,
                        EStorageType.gallery,
                        EStorageType.feed,
                        EStorageType.viewerAttaches,
                        EStorageType.public,
                    ].includes(storage)
                ) {
                    reject();
                    return;
                }

                const { action = '' } = SettingsSelectors.getQueryParams(state);
                if (action) {
                    reject();
                    sendXray(['promo-test', PROMO_VIEWER_POPUP_ID, 'rejectbyaction']);
                    return;
                }

                const isBizUser = UserSelectors.isBizUser(state);
                const isCorpUser = UserSelectors.isCorpUser(state);
                const isPaidUser = UserSelectors.isPaidUser(state);
                const isOverquota = UserQuotaSelectors.isOverquota(state);

                const isReject = isBizUser || isPaidUser || isOverquota || isCorpUser;

                sendXray(['promo-test', PROMO_VIEWER_POPUP_ID, isReject ? 'reject' : 'resolve']);

                if (isReject) {
                    reject();
                    return;
                }

                resolve();
            },
        })
    );
